import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  linkOptions,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
  ArrowRight,
  Ban,
  Calendar,
  Check,
  CircleDot,
  Coins,
  CreditCard,
  Loader2,
  RotateCcw,
  User,
} from "lucide-react";
import { Suspense, useMemo, useState } from "react";
import type { createIndividualCardBase } from "shared";
import {
  type CardLimitIncreaseRequest,
  type CardRequestData,
  capitalize,
} from "shared";
import { toast } from "sonner";
import { createColumnConfigHelper } from "@/components/data-table-filter/core/filters";
import { Page } from "@/components/page";
import { SheetSkeleton } from "@/components/skeletons/sheet-skeleton";
import { RenderCell } from "@/components/table/cells/render-cell";
import { DataTable } from "@/components/table/DataTable";
import { TablePagination } from "@/components/table/DataTablePagination";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePersistedReactTable } from "@/hooks/use-persisted-table";
import { useRequiredRole } from "@/hooks/useRequiredRole";
import { type RouterOutputs, trpc } from "@/lib/trpcClient";
import { AsyncCard } from "./requests/-components/async-card";
import { RejectRequestButton } from "./requests/-components/reject-request-button";

export const Route = createFileRoute("/_authed/_org/requests")({
  component: RouteComponent,
  loader: ({ context: { queryClient, trpc } }) => {
    queryClient.ensureQueryData(trpc.request.list.queryOptions());
  },
});

type CardIssueInput = typeof createIndividualCardBase._type;
type Request = RouterOutputs["request"]["list"][number];

const dtf = createColumnConfigHelper<Request>();

const baseColumnFilters = [
  dtf
    .date()
    .id("Date")
    .displayName("Date")
    .accessor((row) => row.createdAt)
    .icon(Calendar)
    .build(),

  dtf
    .text()
    .id("Member")
    .accessor((row) => row.forUserId)
    .displayName("Member")
    .icon(User)
    .build(),

  dtf
    .option()
    .id("Status")
    .accessor((row) => row.status)
    .displayName("Status")
    .icon(CircleDot)
    .transformOptionFn((u) => ({
      label: capitalize(u),
      value: u,
    }))
    .build(),
];

const cardRequestColumnFilters = [
  dtf
    .number()
    .id("Spend Limit")
    .accessor(
      (row) =>
        (row.data as CardIssueInput).restrictions.periodTargetCents / 100,
    )
    .displayName("Spend Limit")
    .icon(Coins)
    .build(),

  dtf
    .option()
    .id("Refresh Interval")
    .accessor(
      (row) => (row.data as CardIssueInput).restrictions.refreshInterval,
    )
    .displayName("Refresh Interval")
    .icon(RotateCcw)
    .transformOptionFn((u) => ({
      label: capitalize(u),
      value: u,
    }))
    .build(),
];

const limitIncreaseColumnFilters = [
  dtf
    .number()
    .id("New Spend Limit")
    .accessor(
      (row) => (row.data as CardLimitIncreaseRequest).newTargetCents / 100,
    )
    .displayName("Spend Limit")
    .icon(Coins)
    .build(),
];

function RouteComponent() {
  const navigate = Route.useNavigate();
  const location = useLocation();

  const { data, isLoading: isRequestsLoading } = useQuery(
    trpc.request.list.queryOptions(),
  );
  const { data: orgMembers, isLoading: isUsersLoading } = useQuery(
    trpc.org.users.queryOptions(),
  );
  const { admin } = useRequiredRole();

  const isLoading = isRequestsLoading || isUsersLoading;

  const baseTableColumns: ColumnDef<Request & { data: any }>[] = (
    [
      admin && {
        accessorFn: (row) => row.forUserId,
        cell: ({ row }) =>
          orgMembers && (
            <RenderCell
              user={orgMembers.userMap[row.original.forUserId]}
              type="member"
            />
          ),
        id: "Member",
      },
      {
        accessorFn: (row) => row.status,
        cell: ({ row }) => (
          <RenderCell
            type="status"
            statusReason={row.original.statusReason ?? undefined}
            status={row.original.status}
          />
        ),
        id: "Status",
      },
      {
        accessorFn: (row) => row.createdAt,
        cell: ({ row }) => (
          <RenderCell type="date" date={row.original.createdAt} />
        ),
        id: "Date",
      },
      admin && {
        accessorFn: (row) => row.id,
        cell: ({ row }) =>
          row.original.status === "pending" ? (
            <RequestActions id={row.original.id} />
          ) : (
            <span className="text-neutral-500">N/A</span>
          ),
        enableSorting: false,
        id: "Actions",
      },
    ] as ColumnDef<Request>[]
  ).filter(Boolean);

  const cardRequestColumns: ColumnDef<Request & { data: CardIssueInput }>[] = [
    {
      accessorFn: (row) => row.data.name,
      cell: ({ row }) => (
        <RenderCell
          type="card_request"
          cardType={row.original.data.type}
          cardName={row.original.data.name}
          requestId={row.original.id}
          editable={admin && row.original.status === "pending"}
        />
      ),
      id: "Requested Card",
    },
    {
      accessorFn: (row) => row.data.restrictions.periodTargetCents,
      cell: ({ row }) => (
        <RenderCell
          type="spend_limit"
          frequency={row.original.data.restrictions.refreshInterval}
          amount={row.original.data.restrictions.periodTargetCents}
        />
      ),
      id: "Spend Limit",
    },
    {
      accessorFn: (row) => row.data.restrictions.refreshInterval,
      id: "Refresh Interval",
    },
  ];

  const limitIncreaseColumns: ColumnDef<
    Request & { data: CardLimitIncreaseRequest }
  >[] = [
    {
      accessorFn: (row) => row.data.cardId,
      cell: ({ row }) => (
        <AsyncCard cardId={row.original.data.cardId}>
          {(card) => <RenderCell type="card" card={card} />}
        </AsyncCard>
      ),
      id: "Card",
    },
    {
      accessorFn: (row) => row.data.newTargetCents,
      cell: ({ row }) => (
        <AsyncCard cardId={row.original.data.cardId}>
          {(card) => (
            <div className="flex gap-2">
              <div className="opacity-50">
                <RenderCell
                  type="spend_limit"
                  frequency={card.restriction.refreshInterval}
                  amount={card.restriction.periodTargetCents}
                />
              </div>

              <ArrowRight className="mt-1 size-4" />

              <RenderCell
                type="spend_limit"
                frequency={card.restriction.refreshInterval}
                amount={row.original.data.newTargetCents}
              />
            </div>
          )}
        </AsyncCard>
      ),
      id: "Requested Spend Limit",
    },
  ];

  const columnTypes = {
    card: cardRequestColumns,
    limit: limitIncreaseColumns,
  };

  const filterTypes = {
    card: cardRequestColumnFilters,
    limit: limitIncreaseColumnFilters,
  };

  const [requestType, setRequestType] = useState("card");
  const filteredData = useMemo(
    () => data?.filter((r) => r.type === requestType),
    [data, requestType],
  );

  const tableName = "requests";
  const { table, filters, actions, columns, strategy } = usePersistedReactTable(
    {
      columns: [
        ...columnTypes[requestType as keyof typeof columnTypes],
        ...baseTableColumns,
      ],
      data: (filteredData ?? []) as (Request & {
        data: CardRequestData;
      })[],
      filterOptions: {
        columnsConfig: [
          ...filterTypes[requestType as keyof typeof filterTypes],
          ...baseColumnFilters,
        ],
        defaultFilters: [],
        strategy: "client",
      },
      getRowId: (row) => row.id,
      initialState: {
        columnVisibility: {
          "Refresh Interval": false,
        },
      },
      tableName,
    },
  );

  const cardRequestsCount = data?.filter((r) => r.type === "card").length;
  const limitIncreaseRequestsCount = data?.filter(
    (r) => r.type === "limit",
  ).length;

  return (
    <Sheet
      onOpenChange={(open) => {
        if (!open) {
          navigate({
            replace: true,
            to: "/requests",
          });
        }
      }}
      open={
        location.pathname !== "/requests/" &&
        location.pathname.includes("/requests/")
      }
    >
      <Page
        heading={admin ? "Requests" : "My Requests"}
        table={{
          actions,
          columns,
          filters,
          strategy,
          table,
        }}
        tabs={
          <Tabs
            defaultValue={requestType}
            onValueChange={setRequestType}
            className="@sm/page:w-auto w-full"
          >
            <TabsList
              variant="standard"
              className="h-fit @sm/page:w-auto w-full @sm/page:flex-row flex-col [&>button]:w-full [&>button]:py-2"
            >
              <TabsTrigger
                value="card"
                className="min-w-fit"
                count={cardRequestsCount}
                variant="standard"
              >
                <span className="flex items-center gap-2">
                  <CreditCard className="size-3.5 text-neutral-400" />
                  Cards
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="limit"
                className="min-w-fit"
                count={limitIncreaseRequestsCount}
                variant="standard"
              >
                <span className="flex items-center gap-2">
                  <Coins className="size-3.5 text-neutral-400" />
                  Limit Increases
                </span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        }
        actions={
          !admin && (
            <Link to="/requests/create/card">
              <Button>
                <CreditCard />
                Request a new card
              </Button>
            </Link>
          )
        }
      >
        <div className="min-h-0 grow overflow-y-auto">
          <DataTable
            navOptions={(row) =>
              linkOptions({
                params: {
                  id: row.original.id,
                },
                to: "/requests/$id",
              })
            }
            noResultsComponent={
              <div className="flex h-36 w-full flex-col items-center justify-center space-y-4">
                <span className="text-xl">No Matching Requests</span>

                <Button onClick={actions?.removeAllFilters} variant={"outline"}>
                  Clear Filters
                </Button>
              </div>
            }
            table={table}
            tableName={tableName}
            loading={isLoading}
            className="[&_th]:border-t-0!"
          />
        </div>

        <TablePagination tableName={tableName} table={table} />

        <SheetContent
          className="w-full"
          onEscapeKeyDown={(e) => {
            e.preventDefault();
          }}
        >
          <Suspense
            fallback={
              <div className="px-6">
                <SheetSkeleton />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </SheetContent>
      </Page>
    </Sheet>
  );
}

function RequestActions({ id }: { id: string }) {
  const { mutate: approveRequest, isPending: approvalPending } = useMutation(
    trpc.request.approve.mutationOptions({
      onError: async (err) => {
        toast.error(err.message, {
          position: "bottom-left",
        });
      },
      onSuccess: async (_data) => {
        toast.success("Request Approved", {
          position: "bottom-left",
        });
      },
    }),
  );

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        tooltip="Approve"
        className="size-8 text-green-700 hover:text-green-800"
        onClick={() => approveRequest({ requestId: id })}
      >
        {approvalPending ? <Loader2 className="animate-spin" /> : <Check />}
      </Button>

      <RejectRequestButton
        requestId={id}
        render={(rejectionPending) => (
          <Button
            variant="outline"
            tooltip="Reject"
            className="size-8 text-red-400 hover:text-red-600"
          >
            {rejectionPending ? <Loader2 className="animate-spin" /> : <Ban />}
          </Button>
        )}
      />
    </div>
  );
}
